import React, { useEffect, useState } from 'react';
import { FieldProps, getIn } from 'formik';
import TempImagePicker, {
    TempAndPersistedAvatarState,
    avatarStateToAvatarChange,
    useTempAndExistingAvatarState,
} from '../../../v2BaseComponents/ImagePicker/TempImagePicker';
import urls from '../../../../utils/urls';
import { APITypes, MedicalDataTypes } from '../../../../primary-record-types';
import { RESOURCE_IMAGE_UPLOAD_QUALITY } from '../../../../utils/constants';
import { FormValueImage } from './types';
import Stack from '../../../v2BaseComponents/Stack';

type InputUpdateProps =
    | Pick<APITypes.medicalDataService.ResourceImageUpdateExisting, 'imageChange' | 'type'>
    | Pick<APITypes.medicalDataService.ResourceImageCreate, 'type' | 'tempFileId'>;

export type ImageInputComponentProps = {
    value: FormValueImage['imageValue'];
    error?: string;
    resourceId?: string;
    profileId: string;
    category: MedicalDataTypes.PrimaryRecordResourceCategory;
    isDisabled?: boolean;
    onChange: (value: InputUpdateProps) => void;
    testID?: string;
};

const ImageInputComponent: React.FC<ImageInputComponentProps> = (props) => {
    // @ts-expect-error TS2345
    const [avatarState, onAvatarStateUpdate] = useState<TempAndPersistedAvatarState>(null);
    const { tempImagePickerProps } = useTempAndExistingAvatarState({
        existingAvatarUrl:
            props.resourceId && props.profileId && props.value.type === 'update' && props.value.resourceImageId
                ? urls.api.medicalData.resourceImage(
                      props.profileId,
                      props.category,
                      props.resourceId,
                      props.value.resourceImageId,
                  )
                : undefined,
        avatarState,
        onAvatarStateUpdate,
    });

    useEffect(() => {
        if (!avatarState || !props?.value) return;

        if (props.value.type === 'update') {
            props.onChange({
                type: 'update',
                imageChange: avatarStateToAvatarChange(avatarState),
            });
        } else {
            props.onChange({
                type: 'create',
                // @ts-expect-error TS2322
                tempFileId: avatarState.type === 'updated' ? avatarState.value.tempFileId : undefined,
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [avatarState]);

    return (
        <Stack horizontal fullWidth justify="center">
            <TempImagePicker
                {...tempImagePickerProps}
                imagePickerOptions={{
                    quality: RESOURCE_IMAGE_UPLOAD_QUALITY,
                    allowsEditing: true,
                }}
                isDisabled={props.isDisabled}
                error={props.error}
            />
        </Stack>
    );
};

export type ImageInputProps = FieldProps<FormValueImage['imageValue']> & {
    resourceId?: string;
    profileId: string;
    category: MedicalDataTypes.PrimaryRecordResourceCategory;
    isDisabled?: boolean;
    testID?: string;
};

const ImageINput: React.FC<ImageInputProps> = (props) => {
    const {
        field: { name, value },
        form: { setFieldValue },
        ...toPass
    } = props;

    const validationError = getIn(props.form.errors, `${name}`);
    const touched = getIn(props.form.touched, `${name}.type`);
    const error = touched && validationError ? validationError : null;

    return (
        <ImageInputComponent
            value={value}
            onChange={(partialProps) => {
                setFieldValue(name, {
                    ...value,
                    ...partialProps,
                });
            }}
            error={error}
            testID={toPass.testID || name}
            {...toPass}
        />
    );
};

export default React.memo(ImageINput);
